import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useApp } from '../App';
import {
  listenAssignments, listenCourses, listenDailyWindows, fetchSchoolCalendar,
  placeAssignment, unplaceAssignment,
} from './api';
import { weekNumberForDate, todayIso, addDaysIso } from './calendarMath';
import { checkSequencingRules } from './authoring';
import { ANCHORS } from './seed/calendar';
import { nextOrderFor, weekDates as weekDatesFor } from './placement';

// One listener set for the whole school module. Today/This week/Flagged and
// StatusBanner each used to run their own listenAssignments + listenCourses
// effect — five copies of the same query (~2,000 docs each on open), and five
// separate places for the derived counts to drift apart. They did drift: the
// handling of unplaced (date === null) work differed in every one of them.
//
// Takes `student` explicitly, defaulting to familyData.activeChild — the same
// convention WeeklyBoard used — so the owner's tabs, which have no activeChild
// and no router, can mount this around their own content.
//
// Everything week-scoped here means THE CURRENT WEEK, full stop. Views that
// browse other weeks (WeekPage's prev/next) derive their own slice from
// `assignments`; otherwise paging the week view would change what
// StatusBanner reports as "this week".

const EMPTY = Object.freeze([]);
const SchoolDataContext = createContext(null);

export function useSchoolData() {
  const ctx = useContext(SchoolDataContext);
  // Loud on purpose: the owner module renders a dozen tabs, and a missing
  // provider should fail at dev time rather than render an empty week.
  if (!ctx) throw new Error('useSchoolData must be used inside <SchoolDataProvider>');
  return ctx;
}

export function SchoolDataProvider({ student: studentProp, children }) {
  const { familyData } = useApp() || {};
  const student = studentProp || familyData?.activeChild;
  const familyId = familyData?.id;
  const studentEmail = student?.studentEmail;

  // App.jsx's hydrateAsStudent already fetched this student's assignments and
  // courses at sign-in and then nothing read them. Seeding from that turns a
  // wasted read into an instant first paint; the listener supersedes it a
  // moment later. Only valid for the student it hydrated for — an owner's
  // familyData carries no assignments at all.
  const preloaded = familyData?.activeChild?.studentEmail === studentEmail ? familyData : null;
  const [assignments, setAssignments] = useState(() => preloaded?.assignments || null);
  const [courses, setCourses] = useState(() => preloaded?.courses || EMPTY);
  const [calendar, setCalendar] = useState(undefined); // undefined = loading, null = not seeded
  const [windowsDoc, setWindowsDoc] = useState(null);

  // Ids with a placement write in flight. They drop out of unplacedThisWeek
  // immediately, so a child tapping "Mon" four times on a "Cursive x4" row
  // places four different tiles instead of writing to the same one four times
  // while waiting for the snapshot. Deliberately not a disabled/busy state —
  // disabling the row after each tap makes placing four sessions feel broken.
  const [pendingIds, setPendingIds] = useState(() => new Set());

  // Family-wide data — must NOT re-subscribe when the owner switches student
  // in the picker.
  useEffect(() => {
    if (!familyId) return;
    let alive = true;
    const unsubC = listenCourses(familyId, rows => { if (alive) setCourses(rows); });
    const unsubW = listenDailyWindows(familyId, doc => { if (alive) setWindowsDoc(doc); });
    // The `alive` guard matters here: WeekPage/WeeklyBoard/StatusBanner all
    // did `fetchSchoolCalendar(id).then(setCalendar)` unguarded, setting state
    // on a possibly-unmounted component. React 18 stopped warning about it.
    fetchSchoolCalendar(familyId).then(cal => { if (alive) setCalendar(cal ?? null); });
    return () => { alive = false; unsubC(); unsubW(); };
  }, [familyId]);

  // Per-student data.
  useEffect(() => {
    if (!familyId || !studentEmail) return;
    let alive = true;
    setAssignments(null); // don't let the previous student's rows show through
    const unsub = listenAssignments(familyId, studentEmail, rows => { if (alive) setAssignments(rows); });
    return () => { alive = false; unsub(); };
  }, [familyId, studentEmail]);

  const today = todayIso();
  const weeks = calendar?.weeks;
  const all = assignments || EMPTY;

  const weekNumber = useMemo(
    () => (weeks?.length ? weekNumberForDate(weeks, today) : 0),
    [weeks, today]
  );
  const monday = weekNumber > 0 ? weeks[weekNumber - 1] : null;
  const weekDates = useMemo(() => weekDatesFor(monday), [monday]);

  const holidayFor = useCallback(date => calendar?.holidays?.[date], [calendar]);

  // Which of Mon-Fri actually have school this week. Weeks 11 and 15 are
  // two-day weeks (Thanksgiving, winter break) and the seed still emits all 17
  // board tiles for them — the pool says so rather than pretending they fit.
  const instructionalDows = useMemo(
    () => [1, 2, 3, 4, 5].filter(dow => monday && !calendar?.holidays?.[addDaysIso(monday, dow - 1)]),
    [monday, calendar]
  );

  const placedThisWeek = useMemo(
    () => all.filter(a => a.weekNumber === weekNumber && a.date),
    [all, weekNumber]
  );

  const unplacedThisWeek = useMemo(
    () => all.filter(a =>
      a.placementMode === 'student-placed' &&
      a.weekNumber === weekNumber &&
      !a.date &&
      !pendingIds.has(a.id)
    ),
    [all, weekNumber, pendingIds]
  );

  const byDate = useMemo(() => {
    const m = new Map();
    for (const a of all) {
      if (!a.date) continue;
      if (!m.has(a.date)) m.set(a.date, []);
      m.get(a.date).push(a);
    }
    for (const items of m.values()) items.sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
    return m;
  }, [all]);

  const overdue = useMemo(
    () => all.filter(a => a.date && a.date < today && a.status !== 'complete'),
    [all, today]
  );

  const flagged = useMemo(
    () => all.filter(a => a.status === 'flagged' || a.confusionFlag),
    [all]
  );

  const courseById = useMemo(() => new Map(courses.map(c => [c.id, c])), [courses]);
  const courseFor = useCallback(a => courseById.get(a?.courseId), [courseById]);

  const dateForDay = useCallback(dow => (monday ? addDaysIso(monday, dow - 1) : null), [monday]);

  const canPlaceOn = useCallback(
    dow => { const d = dateForDay(dow); return Boolean(d) && !calendar?.holidays?.[d]; },
    [dateForDay, calendar]
  );

  // Soft nudges only — never blocks a placement, per the module's standing
  // "every number is a cap, not a target" rule.
  const warningsFor = useCallback((assignment, dow) => {
    const date = dateForDay(dow);
    if (!date || !assignment) return [];
    return checkSequencingRules({
      dayOfWeek: dow,
      existingToday: (byDate.get(date) || []).filter(a => a.id !== assignment.id),
      item: assignment,
      isAfterSwim: ANCHORS.swim?.days.includes(dow),
    });
  }, [dateForDay, byDate]);

  const place = useCallback(async (assignment, dow) => {
    const date = dateForDay(dow);
    if (!date || !assignment) return;
    if (assignment.date === date) return;        // already there — nothing to write
    if (calendar?.holidays?.[date]) return;      // no school that day
    const id = assignment.id;
    setPendingIds(s => new Set(s).add(id));
    try {
      const order = nextOrderFor(byDate.get(date) || [], id);
      await placeAssignment(familyId, id, date, dow, order);
    } finally {
      setPendingIds(s => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [familyId, dateForDay, calendar, byDate]);

  const unplace = useCallback(async assignment => {
    if (!assignment?.date) return;
    const id = assignment.id;
    setPendingIds(s => new Set(s).add(id));
    try {
      await unplaceAssignment(familyId, id);
    } finally {
      setPendingIds(s => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [familyId]);

  const value = useMemo(() => ({
    familyId,
    student,
    studentEmail,
    assignments: all,
    courses,
    calendar,
    windowsDoc,
    loading: assignments === null || calendar === undefined,
    seeded: Boolean(weeks?.length),
    today,
    weekNumber,
    monday,
    weekDates,
    instructionalDows,
    unplacedThisWeek,
    placedThisWeek,
    byDate,
    overdue,
    flagged,
    pendingIds,
    courseFor,
    holidayFor,
    dateForDay,
    canPlaceOn,
    warningsFor,
    place,
    unplace,
  }), [
    familyId, student, studentEmail, all, assignments, courses, calendar, windowsDoc,
    weeks, today, weekNumber, monday, weekDates, instructionalDows, unplacedThisWeek,
    placedThisWeek, byDate, overdue, flagged, pendingIds, courseFor, holidayFor,
    dateForDay, canPlaceOn, warningsFor, place, unplace,
  ]);

  return <SchoolDataContext.Provider value={value}>{children}</SchoolDataContext.Provider>;
}
